import { DefaultEventHandlers, EventTypes, HISTORY_ACTION_MODE } from '@candulabs/craft';
import { Caption, Inline, Stack } from 'canoe';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import DragIcon from '!!react-svg-loader!./assets/dragIcon.svg';
import { useErrorContext } from 'commons/containers/ValidationErrorManager';
import { useEditor, useEditorSelector } from 'craft/hooks';
import { setFlowStep, useIsPaletteLayersHidden } from 'craft/state';
import { CanduCraftIndicator } from 'craft/types';

import { NodeLayerCanvas } from './NodeLayerCanvas';
import { shiftFlowStep, shiftFormStep } from './shiftNodeOnStepChange';

export const Layer = React.memo(() => {
  const { errors } = useErrorContext();
  // this hook allows to fire on redo/undo action that otherwise change the nodes Set silently, without triggering a re-render of the NodeLayer lib
  useEditorSelector((state) => state.nodes);
  const dispatch = useDispatch();
  const hiddenLayers = useIsPaletteLayersHidden();

  const { query, actions } = useEditor();
  const draggedOver: Set<string> = useEditorSelector((state) => state.events[EventTypes.dragged]);
  const selectedNodes: Set<string> = useEditorSelector(
    (state) => state.events[EventTypes.selected],
  );

  const getNode = (nodeId: string) => query.node(nodeId);
  const onSelectNode = (nodeIds: string[]) => {
    let stepModified = false;
    let i = 0;
    while (!stepModified && i < nodeIds.length) {
      const flowPayload: [string, string, number] | null = shiftFlowStep(nodeIds[i], getNode);
      if (flowPayload) {
        dispatch(setFlowStep(...flowPayload));
        stepModified = true;
      }
      const formPayload = shiftFormStep(nodeIds[i], getNode);
      if (formPayload) {
        dispatch(setFlowStep(...formPayload));
        stepModified = true;
      }
      i += 1;
    }

    if (stepModified === true) {
      window.requestAnimationFrame(() => {
        actions.setNodeEvent(EventTypes.selected, nodeIds);
      });
    } else {
      actions.setNodeEvent(EventTypes.selected, nodeIds);
    }
  };
  const onMoveNode = (draggedNodeId: string[], parentId: string, newIndex: number) => {
    actions.move(draggedNodeId, parentId, newIndex);
    onSelectNode(draggedNodeId);
  };

  const onDragStart = useCallback((selectedElementIds: string[]) => {
    actions.setNodeEvent(EventTypes.dragged, selectedElementIds);
    DefaultEventHandlers.draggedElement = selectedElementIds;
  }, []);

  const onDragEnd = useCallback(() => {
    const { draggedElement, draggedElementShadow, indicator } = DefaultEventHandlers;
    if (draggedElement && indicator && !indicator.error) {
      const { placement } = indicator as CanduCraftIndicator;
      const index = placement.index + (placement.where === 'after' ? 1 : 0);

      onMoveNode(draggedElement as string[], placement.parent.id, index);
    }

    if (draggedElementShadow) {
      draggedElementShadow?.parentNode?.removeChild(draggedElementShadow);
      // @ts-expect-error
      DefaultEventHandlers.draggedElementShadow = null;
    }

    // @ts-expect-error
    DefaultEventHandlers.draggedElement = null;
    // @ts-expect-error
    DefaultEventHandlers.indicator = null;

    actions.setIndicator(null);
    actions.setNodeEvent(EventTypes.draggedOver, [], HISTORY_ACTION_MODE.IGNORE);
    actions.setNodeEvent(EventTypes.dragged, []);
  }, []);

  if (hiddenLayers) {
    return (
      <Inline mt="xxl" mr="normal" ml="xxs" fillSpace>
        <Stack space="s" justify="center">
          <DragIcon />
          <Caption color="OnyxGray" textAlign="center">
            You&apos;re dragging something in the toolbox. Drop it to update and preview your
            layers.
          </Caption>
        </Stack>
      </Inline>
    );
  }

  return (
    <NodeLayerCanvas
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onMoveNode={onMoveNode}
      onSelectNode={onSelectNode}
      errorNodes={errors}
      selectedNodes={selectedNodes}
      externalDraggedItems={draggedOver}
      getNode={getNode}
    />
  );
});

Layer.displayName = 'Layer';
